import { DBVideoGumletAssetIdSchema } from './schemas';
import { VideoPlaybackProvider } from './enums';

/**
 * The source fields of a video record, as they may arrive: from a stored row,
 * from a create body, or merged from a stored row and a partial update.
 */
export interface VideoSourceFields {
  playbackProvider?: string | undefined;
  youtubeId?: string | undefined;
  gumletAssetId?: string | undefined;
}

export enum VideoSourceProblem {
  /** `playbackProvider` holds a value this build does not know. */
  UnknownProvider = 'unknownProvider',
  /** The provider's own identifier is missing. */
  MissingId = 'missingId',
  /** Identifiers for more than one provider are present. */
  AmbiguousSource = 'ambiguousSource',
}

export type VideoSourceResolution =
  | { valid: true; provider: VideoPlaybackProvider.YouTube; youtubeId: string }
  | { valid: true; provider: VideoPlaybackProvider.Gumlet; gumletAssetId: string }
  | { valid: false; problem: VideoSourceProblem; message: string };

const isPresent = (value: string | undefined): value is string =>
  typeof value === 'string' && value.length > 0;

/**
 * Decides which provider a complete source describes, and refuses anything that
 * is not one whole source. An absent `playbackProvider` is read as YouTube —
 * that is the only legacy shape — unless a Gumlet asset ID is present; an
 * unrecognised provider is refused rather than defaulted, so a corrupt value
 * can never resolve to an unprotected player.
 */
export const resolveVideoSource = (source: VideoSourceFields): VideoSourceResolution => {
  const { playbackProvider, youtubeId, gumletAssetId } = source;

  const provider = isPresent(playbackProvider)
    ? playbackProvider
    : // A record with no provider and a Gumlet ID is not a legacy YouTube row,
      // so it is refused below rather than read as one.
      isPresent(gumletAssetId)
      ? VideoPlaybackProvider.Gumlet
      : VideoPlaybackProvider.YouTube;

  switch (provider) {
    case VideoPlaybackProvider.YouTube:
      if (isPresent(gumletAssetId)) {
        return {
          valid: false,
          problem: VideoSourceProblem.AmbiguousSource,
          message: 'A YouTube video cannot also carry a Gumlet asset ID',
        };
      }
      if (!isPresent(youtubeId)) {
        return {
          valid: false,
          problem: VideoSourceProblem.MissingId,
          message: 'A YouTube video needs a YouTube ID',
        };
      }
      return { valid: true, provider: VideoPlaybackProvider.YouTube, youtubeId };

    case VideoPlaybackProvider.Gumlet:
      if (isPresent(youtubeId)) {
        return {
          valid: false,
          problem: VideoSourceProblem.AmbiguousSource,
          message: 'A Gumlet video cannot also carry a YouTube ID',
        };
      }
      if (!isPresent(gumletAssetId)) {
        return {
          valid: false,
          problem: VideoSourceProblem.MissingId,
          message: 'A Gumlet video needs a Gumlet asset ID',
        };
      }
      if (!isPresent(playbackProvider)) {
        return {
          valid: false,
          problem: VideoSourceProblem.MissingId,
          message: 'A Gumlet video needs a playback provider',
        };
      }
      return { valid: true, provider: VideoPlaybackProvider.Gumlet, gumletAssetId };

    default:
      return {
        valid: false,
        problem: VideoSourceProblem.UnknownProvider,
        message: `Unknown video playback provider: ${playbackProvider}`,
      };
  }
};

/**
 * The source a partial update would leave behind. A key present in the update
 * wins even when its value is `undefined`, which is how a field is cleared.
 */
export const mergeVideoSource = (
  existing: VideoSourceFields,
  update: VideoSourceFields
): VideoSourceFields => ({
  playbackProvider:
    'playbackProvider' in update ? update.playbackProvider : existing.playbackProvider,
  youtubeId: 'youtubeId' in update ? update.youtubeId : existing.youtubeId,
  gumletAssetId: 'gumletAssetId' in update ? update.gumletAssetId : existing.gumletAssetId,
});

/**
 * Gumlet link parsing
 */

export const GUMLET_EMBED_ORIGIN = 'https://play.gumlet.io';
const GUMLET_EMBED_HOSTNAME = 'play.gumlet.io';
const GUMLET_EMBED_SEGMENT = 'embed';

/** Longer than any embed link Gumlet's share dialog produces. */
export const MAX_GUMLET_VIDEO_INPUT_LENGTH = 2048;

export enum GumletLinkProblem {
  Empty = 'empty',
  TooLong = 'tooLong',
  NotAGumletLink = 'notAGumletLink',
  WrongOrigin = 'wrongOrigin',
  WrongPath = 'wrongPath',
  InvalidAssetId = 'invalidAssetId',
}

export type GumletLinkParse =
  | { valid: true; assetId: string }
  | { valid: false; problem: GumletLinkProblem; message: string };

const invalid = (problem: GumletLinkProblem, message: string): GumletLinkParse => ({
  valid: false,
  problem,
  message,
});

/**
 * Turns what an admin pasted into a Gumlet asset ID, accepting the embed link
 * from Gumlet's Embed & Share dialog or a bare asset ID.
 *
 * The link is only ever read: its query string is discarded rather than stored
 * (a pasted share link carries an expiring signature), and nothing here
 * fetches it or follows a redirect. The origin is compared whole, so a
 * lookalike host cannot supply the asset ID that later gets signed.
 */
export const parseGumletVideoInput = (input: string): GumletLinkParse => {
  const trimmed = input.trim();

  if (trimmed.length === 0) {
    return invalid(GumletLinkProblem.Empty, 'Enter a Gumlet video link or ID');
  }
  if (trimmed.length > MAX_GUMLET_VIDEO_INPUT_LENGTH) {
    return invalid(GumletLinkProblem.TooLong, 'That Gumlet video link is too long');
  }

  if (DBVideoGumletAssetIdSchema.safeParse(trimmed).success) {
    return { valid: true, assetId: trimmed };
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return invalid(
      GumletLinkProblem.NotAGumletLink,
      `Paste the Gumlet embed link (${GUMLET_EMBED_ORIGIN}/embed/…) or the asset ID`
    );
  }

  if (
    url.protocol !== 'https:' ||
    url.hostname !== GUMLET_EMBED_HOSTNAME ||
    url.port !== '' ||
    url.username !== '' ||
    url.password !== ''
  ) {
    return invalid(
      GumletLinkProblem.WrongOrigin,
      `A Gumlet embed link starts with ${GUMLET_EMBED_ORIGIN}/embed/`
    );
  }

  const segments = url.pathname.split('/').filter(segment => segment.length > 0);
  if (segments.length !== 2 || segments[0] !== GUMLET_EMBED_SEGMENT) {
    return invalid(
      GumletLinkProblem.WrongPath,
      `Use the embed link from Gumlet's Embed & Share dialog, not a download or playlist link`
    );
  }

  const assetId = segments[1]!;
  if (!DBVideoGumletAssetIdSchema.safeParse(assetId).success) {
    return invalid(GumletLinkProblem.InvalidAssetId, 'That link holds no Gumlet asset ID');
  }

  return { valid: true, assetId };
};
